export type WalkthroughStep = {
  id: string;
  targetSelector: string;
  includeIfSelectorExists?: string;
  title: string;
  /**
   * Short, benefit-first one-liner (≤ ~12 words) shown by default. When set,
   * the panel shows this and tucks the longer `description` behind a
   * "Learn more" expander so steps read fast and don't feel monotonous.
   * When omitted, `description` is shown inline (legacy behavior).
   */
  summary?: string;
  description: string;
  panelPosition: 'right' | 'bottom' | 'left' | 'top' | 'auto';
  panelMode: 'floating' | 'inline' | 'embedded';
  panelWidth?: number;
  inlinePlacement?: 'below' | 'inside';
  sharedPanelLayoutId?: string;
  padding: number;
  spotlightPadding?: number;
  spotlightPaddingX?: number;
  spotlightPaddingY?: number;
  noRadius?: boolean;
  /**
   * 'attention' = louder pulse for one-off coachmarks that need to grab the eye
   * more than a regular onboarding tour step. Defaults to 'normal'.
   */
  pulseIntensity?: 'normal' | 'attention';
  /**
   * When true, scroll the target into view on activation. Useful for ephemeral
   * coachmarks where the action area might be off-screen.
   */
  scrollIntoView?: boolean;
  /**
   * When true, dispatch synthetic mouse/pointer enter events on the target so
   * any hover-driven handlers (tooltips, expanding menus, hover previews) fire
   * for the duration of the step. Also sets `data-walkthrough-spotlight` on
   * the target so components can mirror their hover styles via CSS.
   */
  previewHover?: boolean;
};

export type WalkthroughPage = {
  key: string;
  routePattern: string; // regex pattern to match against pathname
  steps: WalkthroughStep[];
  mobileSteps?: WalkthroughStep[];
  autoStart?: boolean;
  readySelector?: string;
  mobileReadySelector?: string;
};

export const WALKTHROUGH_PAGES: WalkthroughPage[] = [
  {
    key: 'avatar-create-chooser',
    routePattern: '/avatars/new$',
    readySelector: '[data-walkthrough="avatar-create-chooser-builder-card"]',
    mobileReadySelector:
      '[data-walkthrough="avatar-create-chooser-builder-card"]',
    steps: [
      {
        id: 'avatar-create-chooser-builder-card',
        targetSelector:
          '[data-walkthrough="avatar-create-chooser-builder-card"]',
        title: 'Build your Avatar',
        summary: 'Define identity, body, face, and hair by hand.',
        description:
          'Choose this path when you want to define the avatar with structured controls for identity, body, face, and hair.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-create-chooser-photo-card',
        targetSelector: '[data-walkthrough="avatar-create-chooser-photo-card"]',
        title: 'Create an AI Twin',
        summary: 'Anchor a realistic twin to your photos.',
        description:
          'Choose this path when you already have clean photos and want them to anchor a realistic AI twin.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-create-chooser-quick-compose',
        targetSelector:
          '[data-walkthrough="avatar-create-chooser-quick-compose"]',
        title: 'Quick Prompt And References',
        summary: 'Describe the character; add references if useful.',
        description:
          'Write the character prompt here and attach reference images if they help define the direction. The images are optional.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-create-chooser-quick-cta',
        targetSelector: '[data-walkthrough="avatar-create-chooser-quick-cta"]',
        title: 'Generate Preview',
        summary: 'Continue with your prompt and references.',
        description:
          'Use this button to continue with the prompt and references you entered here.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
    ],
    mobileSteps: [
      {
        id: 'avatar-create-chooser-builder-card',
        targetSelector:
          '[data-walkthrough="avatar-create-chooser-builder-card"]',
        title: 'Build your Avatar',
        summary: 'Define identity, body, face, and hair by hand.',
        description:
          'Use the structured builder when you want to define the character step by step.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-create-chooser-photo-card',
        targetSelector: '[data-walkthrough="avatar-create-chooser-photo-card"]',
        title: 'Create an AI Twin',
        summary: 'Anchor a realistic twin to your photos.',
        description:
          'Use this path when real photos should define a realistic AI twin.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-create-chooser-quick-compose',
        targetSelector:
          '[data-walkthrough="avatar-create-chooser-quick-compose"]',
        title: 'Quick Prompt And References',
        summary: 'Describe the character; add references if useful.',
        description:
          'Add a prompt here and include reference images only if they help. The images are optional.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-create-chooser-quick-cta',
        targetSelector: '[data-walkthrough="avatar-create-chooser-quick-cta"]',
        title: 'Generate Preview',
        summary: 'Continue with your prompt and references.',
        description:
          'Use this button to continue with the prompt and references you entered here.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
    ],
  },
  {
    key: 'avatar-builder-create',
    routePattern: '/avatars/new/builder$',
    readySelector:
      '[data-walkthrough="avatar-builder-stepper"], [data-walkthrough="avatar-builder-history"]',
    mobileReadySelector:
      '[data-walkthrough="avatar-builder-stepper"], [data-walkthrough="avatar-builder-preview"]',
    steps: [
      {
        id: 'avatar-builder-stepper',
        targetSelector: '[data-walkthrough="avatar-builder-stepper"]',
        includeIfSelectorExists: '[data-walkthrough="avatar-builder-stepper"]',
        title: 'Section Navigation',
        summary: 'Move between builder sections here.',
        description:
          'Use this stepper to move between the builder sections and control which part of the avatar you are editing.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-builder-history',
        targetSelector: '[data-walkthrough="avatar-builder-history"]',
        includeIfSelectorExists: '[data-walkthrough="avatar-builder-history"]',
        title: 'Preview History',
        summary: 'Reopen any past generation to restore it.',
        description:
          'Older preview generations are stored in this column. Click any version to restore the draft selections that produced that generation.',
        panelPosition: 'right',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-builder-step-content',
        targetSelector: '[data-walkthrough="avatar-builder-step-content"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-builder-step-content"]',
        title: 'Builder Workspace',
        summary: 'Set the avatar traits for this draft.',
        description:
          'This is the current builder section where you set the avatar traits for the draft.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-builder-preview',
        targetSelector: '[data-walkthrough="avatar-builder-preview"]',
        includeIfSelectorExists: '[data-walkthrough="avatar-builder-preview"]',
        title: 'Active Preview',
        summary: 'The generation currently selected for this draft.',
        description:
          'This preview shows the currently selected generation for the draft so you can inspect the result before making more changes.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-builder-review-panel',
        targetSelector: '[data-walkthrough="avatar-builder-review"]',
        includeIfSelectorExists: '[data-walkthrough="avatar-builder-review"]',
        title: 'Review Panel',
        summary: 'Inspect and tweak the active draft details.',
        description:
          'Use this panel to inspect and adjust the current draft details for the active preview, including character traits, references, and review settings.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-builder-actions',
        targetSelector:
          '[data-walkthrough="avatar-builder-step-footer"], [data-walkthrough="avatar-builder-review-actions"]',
        title: 'Primary Actions',
        summary: 'Continue, generate, apply edits, or confirm.',
        description:
          'Use these controls to move the draft forward, whether that means continuing, generating a preview, applying edits, or confirming the avatar.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
    ],
    mobileSteps: [
      {
        id: 'avatar-builder-stepper',
        targetSelector: '[data-walkthrough="avatar-builder-stepper"]',
        includeIfSelectorExists: '[data-walkthrough="avatar-builder-stepper"]',
        title: 'Section Navigation',
        summary: 'Move between builder sections here.',
        description:
          'Use this stepper to move between the builder sections and control which part of the avatar you are editing.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-builder-main-workspace',
        targetSelector:
          '[data-walkthrough="avatar-builder-step-content"], [data-walkthrough="avatar-builder-preview"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-builder-step-content"], [data-walkthrough="avatar-builder-preview"]',
        title: 'Main Workspace',
        summary: 'Your builder inputs or the preview, depending on state.',
        description:
          'This is the main area for the current builder state, showing either the builder inputs or the preview you are reviewing.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-builder-review-panel',
        targetSelector: '[data-walkthrough="avatar-builder-review"]',
        includeIfSelectorExists: '[data-walkthrough="avatar-builder-review"]',
        title: 'Review Panel',
        summary: 'Inspect and tweak the active draft details.',
        description:
          'This panel holds the editable draft details for the active preview so you can refine the character before acting on it.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-builder-actions',
        targetSelector:
          '[data-walkthrough="avatar-builder-step-footer"], [data-walkthrough="avatar-builder-review-actions"]',
        title: 'Primary Actions',
        summary: 'Continue, generate, apply edits, or confirm.',
        description:
          'Use these controls to continue the flow, generate a preview, apply edits, or confirm the avatar.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
    ],
  },
  {
    key: 'avatar-from-photos-create',
    routePattern: '/avatars/new/from-photos$',
    readySelector:
      '[data-walkthrough="avatar-from-photos-uploads"], [data-walkthrough="avatar-from-photos-history"]',
    mobileReadySelector:
      '[data-walkthrough="avatar-from-photos-uploads"], [data-walkthrough="avatar-from-photos-preview"]',
    steps: [
      {
        id: 'avatar-from-photos-uploads',
        targetSelector: '[data-walkthrough="avatar-from-photos-uploads"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-from-photos-uploads"]',
        title: 'AI Twin Photo Uploads',
        summary: 'Upload key angles to anchor the identity.',
        description:
          'Upload the key reference angles here so the draft is anchored to the identity you want the AI Twin to preserve.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-from-photos-history',
        targetSelector: '[data-walkthrough="avatar-from-photos-history"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-from-photos-history"]',
        title: 'Preview History',
        summary: 'Reopen any past generation to restore it.',
        description:
          'Older preview generations are stored in this column. Click any version to restore the draft selections that produced that generation.',
        panelPosition: 'right',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-from-photos-advanced',
        targetSelector: '[data-walkthrough="avatar-from-photos-advanced"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-from-photos-advanced"]',
        title: 'Advanced Inputs',
        summary: 'Optional prompt notes and extra references.',
        description:
          'Use this area for optional prompt notes and extra reference images when the photo set needs more direction.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-from-photos-preview',
        targetSelector: '[data-walkthrough="avatar-from-photos-preview"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-from-photos-preview"]',
        title: 'Active Preview',
        summary: 'The generation currently selected for this draft.',
        description:
          'This preview shows the currently selected generation for the draft so you can inspect the result before making more changes.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-from-photos-review-panel',
        targetSelector: '[data-walkthrough="avatar-from-photos-review"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-from-photos-review"]',
        title: 'Review Panel',
        summary: 'Inspect and tweak the active draft details.',
        description:
          'Use this panel to review and adjust the current draft details tied to the active preview, including identity references, extra prompt context, and character settings.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-from-photos-actions',
        targetSelector:
          '[data-walkthrough="avatar-from-photos-generate"], [data-walkthrough="avatar-from-photos-review-actions"]',
        title: 'Primary Actions',
        summary: 'Continue, generate, apply edits, or confirm.',
        description:
          'Use these controls to generate the first preview, apply your latest review edits, or confirm the avatar when it is ready.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
    ],
    mobileSteps: [
      {
        id: 'avatar-from-photos-uploads',
        targetSelector: '[data-walkthrough="avatar-from-photos-uploads"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-from-photos-uploads"]',
        title: 'AI Twin Photo Uploads',
        summary: 'Upload key angles to anchor the identity.',
        description:
          'Upload the key reference angles here so the draft is anchored to the identity you want the AI Twin to preserve.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-from-photos-main-workspace',
        targetSelector:
          '[data-walkthrough="avatar-from-photos-advanced"], [data-walkthrough="avatar-from-photos-preview"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-from-photos-advanced"], [data-walkthrough="avatar-from-photos-preview"]',
        title: 'Main Workspace',
        summary: 'Your builder inputs or the preview, depending on state.',
        description:
          'This is the main area for the current flow state, showing either the optional advanced inputs or the preview you are reviewing.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-from-photos-review-panel',
        targetSelector: '[data-walkthrough="avatar-from-photos-review"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-from-photos-review"]',
        title: 'Review Panel',
        summary: 'Inspect and tweak the active draft details.',
        description:
          'This panel holds the editable draft details for the active preview so you can refine the character before taking the next action.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-from-photos-actions',
        targetSelector:
          '[data-walkthrough="avatar-from-photos-generate"], [data-walkthrough="avatar-from-photos-review-actions"]',
        title: 'Primary Actions',
        summary: 'Continue, generate, apply edits, or confirm.',
        description:
          'Use these controls to generate a preview, apply edits, or confirm the avatar.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
    ],
  },
  {
    key: 'avatar-quick-generate-create',
    routePattern: '/avatars/new/quick-generate$',
    readySelector:
      '[data-walkthrough="avatar-quick-generate-composer"], [data-walkthrough="avatar-quick-generate-history"]',
    mobileReadySelector:
      '[data-walkthrough="avatar-quick-generate-composer"], [data-walkthrough="avatar-quick-generate-preview"]',
    steps: [
      {
        id: 'avatar-quick-generate-composer',
        targetSelector: '[data-walkthrough="avatar-quick-generate-composer"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-quick-generate-composer"]',
        title: 'Prompt And References',
        summary: 'Write the direction; add references to sharpen it.',
        description:
          'Write the character direction here and attach supporting reference images if they help sharpen the result.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-quick-generate-history',
        targetSelector: '[data-walkthrough="avatar-quick-generate-history"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-quick-generate-history"]',
        title: 'Preview History',
        summary: 'Reopen any past generation to restore it.',
        description:
          'Older preview generations are stored in this column. Click any version to restore the draft selections that produced that generation.',
        panelPosition: 'right',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-quick-generate-preview',
        targetSelector: '[data-walkthrough="avatar-quick-generate-preview"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-quick-generate-preview"]',
        title: 'Active Preview',
        summary: 'The generation currently selected for this draft.',
        description:
          'This preview shows the currently selected generation for the draft so you can inspect the result before making more changes.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-quick-generate-review-panel',
        targetSelector: '[data-walkthrough="avatar-quick-generate-review"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-quick-generate-review"]',
        title: 'Review Panel',
        summary: 'Inspect and tweak the active draft details.',
        description:
          'Use this panel to review and adjust the current draft details for the active preview, including supporting references and prompt refinements.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-quick-generate-actions',
        targetSelector:
          '[data-walkthrough="avatar-quick-generate-generate"], [data-walkthrough="avatar-quick-generate-review-actions"]',
        title: 'Primary Actions',
        summary: 'Continue, generate, apply edits, or confirm.',
        description:
          'Use these controls to generate a preview from the current prompt, apply your latest edits, or confirm the avatar.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
    ],
    mobileSteps: [
      {
        id: 'avatar-quick-generate-composer',
        targetSelector: '[data-walkthrough="avatar-quick-generate-composer"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-quick-generate-composer"]',
        title: 'Prompt And References',
        summary: 'Write the direction; add references to sharpen it.',
        description:
          'Write the character direction here and attach supporting reference images if they help sharpen the result.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-quick-generate-main-workspace',
        targetSelector: '[data-walkthrough="avatar-quick-generate-preview"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-quick-generate-preview"]',
        title: 'Active Preview',
        summary: 'The generation currently selected for this draft.',
        description:
          'This preview shows the currently selected generation for the draft so you can inspect the result before making more changes.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-quick-generate-review-panel',
        targetSelector: '[data-walkthrough="avatar-quick-generate-review"]',
        includeIfSelectorExists:
          '[data-walkthrough="avatar-quick-generate-review"]',
        title: 'Review Panel',
        summary: 'Inspect and tweak the active draft details.',
        description:
          'This panel holds the editable draft details for the active preview so you can refine the direction before taking the next action.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-quick-generate-actions',
        targetSelector:
          '[data-walkthrough="avatar-quick-generate-generate"], [data-walkthrough="avatar-quick-generate-review-actions"]',
        title: 'Primary Actions',
        summary: 'Continue, generate, apply edits, or confirm.',
        description:
          'Use these controls to generate a preview, apply edits, or confirm the avatar.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
    ],
  },
  {
    key: 'avatar-detail',
    routePattern: '/files/avatars/[^/]+$',
    autoStart: false,
    readySelector: '[data-walkthrough="avatar-detail-actions"]',
    mobileReadySelector: '[data-walkthrough="avatar-detail-actions"]',
    steps: [
      {
        id: 'avatar-detail-header',
        targetSelector: '[data-walkthrough="avatar-detail-header"]',
        title: 'Avatar Model Card',
        summary: 'One character: identity, prompt, and shot set.',
        description:
          'This page is the canonical reference pack for one character. It keeps the avatar identity, prompt, visibility, and generated shot set together in one place.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-detail-actions',
        targetSelector: '[data-walkthrough="avatar-detail-actions"]',
        title: 'Edit, Publish, And Regenerate',
        summary: 'Refine, publish, regenerate, or delete.',
        description:
          'Use these controls to refine the avatar details, manage publishing, regenerate the pack, or delete it. This is where you keep the character usable and shareable over time.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-detail-gallery',
        targetSelector: '[data-walkthrough="avatar-detail-gallery"]',
        title: 'Standardized Shot Gallery',
        summary: 'Consistent face and body shots to build on.',
        description:
          'These face and body shots are intentionally structured to stay consistent. They give downstream product and marketing generations a reliable visual identity to build on.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
    ],
    mobileSteps: [
      {
        id: 'avatar-detail-header',
        targetSelector: '[data-walkthrough="avatar-detail-header"]',
        title: 'Avatar Model Card',
        summary: 'One character: identity, prompt, and shot set.',
        description:
          'This page keeps one avatar identity and its generated reference pack together.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-detail-actions',
        targetSelector: '[data-walkthrough="avatar-detail-actions"]',
        title: 'Manage The Avatar',
        summary: 'Edit, publish, or regenerate the pack.',
        description:
          'Edit details, publish the avatar, or regenerate the pack from this action row.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'avatar-detail-gallery',
        targetSelector: '[data-walkthrough="avatar-detail-gallery"]',
        title: 'Reference Shot Gallery',
        summary: 'Standardized shots that keep the character consistent.',
        description:
          'This gallery holds the standardized shots that keep the character consistent across future generations.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
    ],
  },
  {
    key: 'design',
    routePattern: '/design$',
    steps: [
      {
        id: 'prompt-input',
        targetSelector: '[data-walkthrough="prompt-input"]',
        title: 'Your Creative Prompt',
        summary: 'Type what you want — drop in files for context.',
        description:
          'This is where everything starts. Describe what you want to create, then drop or paste files directly into the composer. Selected references, workspace files, and output choices appear as chips so the request carries its context with it.',
        panelPosition: 'bottom',
        panelMode: 'inline',
        padding: 4,
      },
      {
        id: 'plus-actions',
        targetSelector: '[data-walkthrough="plus-actions"]',
        title: 'Add Context',
        summary: 'Add photos, Brands, colors, and more before sending.',
        description:
          'Open this menu to add product photos, design or aesthetic direction, Brand references, color palettes, trend data, workspace files, recent uploads, and connected extensions before you send.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 6,
        previewHover: true,
      },
      {
        id: 'composer-action',
        targetSelector:
          '[data-walkthrough="voice-call"], [data-walkthrough="send-output"]',
        title: 'Send Or Call',
        summary: 'Send your prompt, or start a live IMAI call.',
        description:
          'This action changes with your context. Start a live IMAI call when the composer is empty, or send once you have text or references. The send menu can also request a canvas, presentation, or document.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 6,
        previewHover: true,
      },
      {
        id: 'project-context',
        targetSelector: '[data-walkthrough="project-context"]',
        title: 'Project Context',
        summary: 'Group this chat under a project to find it later.',
        description:
          'Choose the project this chat should belong to, or start without one. Related threads and generated work stay easier to find when they share the same project.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 6,
      },
      {
        id: 'visibility-toggle',
        targetSelector: '[data-walkthrough="visibility-toggle"]',
        title: 'Private or Shared',
        summary: 'Keep it private, or share with your workspace.',
        description:
          'Control who sees your work. "Only me" keeps your thread private. Switch to "Workspace" to collaborate with your team in real time.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 3,
      },
      {
        id: 'deep-research',
        targetSelector: '[data-walkthrough="deep-research-toggle"]',
        title: 'Deep Research',
        summary: 'Slower, source-backed answers when you need them.',
        description:
          'Turn this on when a request needs a slower, source-backed research pass before IMAI answers or generates. Leave it off for quick creative iteration.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 6,
      },
      {
        id: 'mode-switcher',
        targetSelector: '[data-walkthrough="mode-switcher"]',
        includeIfSelectorExists: '[data-walkthrough="mode-switcher"]',
        title: 'Two Modes, One Workflow',
        summary: 'Switch between designing products and marketing them.',
        description:
          'Product Design generates new concepts, patterns, and collections from your prompts. Product Studio takes your existing products and creates marketing-ready photos, social posts, and campaign assets. Switch between them anytime.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 3,
      },
      {
        id: 'threads-sidebar',
        targetSelector: '[data-walkthrough="threads-sidebar"]',
        title: 'Your Workspace',
        summary: 'All your chats, products, and assets live here.',
        description:
          'Every conversation becomes a project. Your design threads, product catalog, references, Brand assets, and tech-pack projects all live here. Start a new chat anytime — your previous work is always one click away.',
        panelPosition: 'right',
        panelMode: 'floating',
        padding: 1,
        noRadius: true,
      },
      {
        id: 'workspace-nav',
        targetSelector: '[data-walkthrough="workspace-nav"]',
        title: 'Workspace Navigation',
        summary: 'Jump between chats, studio, avatars, and more.',
        description:
          'Jump between the main work areas here: new chats, product studio, avatars, Brands, the library, workspace files, documents, and enabled workspace tools.',
        panelPosition: 'right',
        panelMode: 'floating',
        padding: 1,
      },
      {
        id: 'products-nav',
        targetSelector: '[data-walkthrough="products-nav"]',
        title: 'Product Catalog',
        summary: 'Every product you make or upload lives here.',
        description:
          'Products you create or upload live here. Each product holds all its generated variations, marketing assets, and metadata — a single source of truth you can keep building on.',
        panelPosition: 'right',
        panelMode: 'floating',
        padding: 1,
      },
    ],
    mobileSteps: [
      {
        id: 'prompt-input',
        targetSelector: '[data-walkthrough="prompt-input"]',
        title: 'Your Creative Prompt',
        summary: 'Type what you want — drop in files for context.',
        description:
          'Describe what you want to create, then attach or paste files right into the composer. Selected references and output choices appear as chips.',
        panelPosition: 'top',
        panelMode: 'inline',
        padding: 4,
      },
      {
        id: 'plus-actions',
        targetSelector: '[data-walkthrough="plus-actions"]',
        title: 'Add Context',
        summary: 'Add photos, Brands, colors, and more before sending.',
        description:
          'Add product photos, aesthetics, Brand references, colors, trends, workspace files, recent uploads, and connected extensions before you send.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 6,
        previewHover: true,
      },
      {
        id: 'composer-action',
        targetSelector:
          '[data-walkthrough="voice-call"], [data-walkthrough="send-output"]',
        title: 'Send Or Call',
        summary: 'Send your prompt, or start a live IMAI call.',
        description:
          'Use the right action to call IMAI or send the prompt. When you send, the output menu can ask for a canvas, presentation, or document.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 6,
        previewHover: true,
      },
      {
        id: 'project-context',
        targetSelector: '[data-walkthrough="project-context"]',
        title: 'Project Context',
        summary: 'Group this chat under a project to find it later.',
        description:
          'Choose the project this chat belongs to, or start without one. Shared project context keeps related work together.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 6,
      },
      {
        id: 'visibility-toggle',
        targetSelector: '[data-walkthrough="visibility-toggle"]',
        title: 'Private or Shared',
        summary: 'Keep it private, or share with your workspace.',
        description:
          'Control who sees your work. "Only me" keeps it private, "Workspace" shares with your team.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 3,
      },
      {
        id: 'deep-research',
        targetSelector: '[data-walkthrough="deep-research-toggle"]',
        title: 'Deep Research',
        summary: 'Slower, source-backed answers when you need them.',
        description:
          'Turn this on for slower, source-backed research. Leave it off when you want fast creative iteration.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 6,
      },
      {
        id: 'mode-switcher',
        targetSelector: '[data-walkthrough="mode-switcher"]',
        includeIfSelectorExists: '[data-walkthrough="mode-switcher"]',
        title: 'Two Modes',
        summary: 'Switch between designing products and marketing them.',
        description:
          'Product Design generates new concepts. Product Studio creates marketing-ready assets from existing products.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 3,
      },
      {
        id: 'mobile-menu',
        targetSelector: '[data-walkthrough="mobile-menu"]',
        title: 'Your Workspace',
        summary: 'All your chats, products, and assets live here.',
        description:
          'Tap here to access your design threads, product catalog, references, Brand assets, and settings.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 6,
      },
    ],
  },
  {
    key: 'product',
    routePattern: '/products/(?!add$)[^/]+$',
    steps: [
      {
        id: 'product-suggestions',
        targetSelector: '[data-walkthrough="product-suggestions"]',
        title: 'Generate Assets',
        summary: 'One-click batches: listings, lifestyle, ads, UGC.',
        description:
          'One-click generation for different use cases — product listings, lifestyle shots, social ads, UGC content, and more. Pick a type and IMAI generates a batch instantly.',
        panelPosition: 'right',
        panelMode: 'floating',
        padding: 0,
        spotlightPaddingX: 12,
        spotlightPaddingY: 8,
      },
      {
        id: 'product-avatars',
        targetSelector: '[data-walkthrough="product-avatars"]',
        title: 'Create Or Use Avatars',
        summary: 'Add up to 5 avatars for lifestyle and social shots.',
        description:
          'This section lets you build a consistent character workflow for the product. If you do not have avatars yet, jump to Design to create them. If you do, select up to 5 here and use them for lifestyle and social image generations.',
        panelPosition: 'right',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'product-tabs',
        targetSelector: '[data-walkthrough="product-tabs"]',
        title: 'Choose Your Work Surface',
        summary: 'Each tab is a different work mode for this product.',
        description:
          'These tabs do more than organize outputs. They switch the product into different working modes. Media Studio is for visual asset creation, Catalogue Data is for commerce-ready product information, Prints & Patterns is for repeatable design exploration, and tech-pack is for production and manufacturing workflows.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'product-gallery',
        targetSelector: '[data-walkthrough="product-gallery"]',
        title: 'Your Asset Gallery',
        summary: 'Every image, video, and 3D model lands here.',
        description:
          'Every generated image, video, and 3D model lives here. Hover over any asset to download, upscale, edit with inpainting, or mention it in chat for further refinement.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'product-add-asset',
        targetSelector: '[data-walkthrough="product-add-asset"]',
        title: 'Add or Generate Assets',
        summary: 'Upload or generate assets for this group.',
        description:
          'Upload your own photos and videos, or generate new ones for this group. Each asset group organizes your content by campaign, style, or use case.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'product-chat-tabs',
        targetSelector: '[data-walkthrough="product-chat-tabs"]',
        title: 'Chat Workspace',
        summary: 'A product-aware chat, not a generic one.',
        description:
          'This area is a product-specific workspace, not a generic chat. The active thread keeps you inside one line of thinking, while the Chats view lets you move between saved conversations for this product and version.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'product-chat-new',
        targetSelector: '[data-walkthrough="product-chat-new"]',
        title: 'Branch Into New Directions',
        summary: 'Start a fresh chat without losing this one.',
        description:
          'Use New chat to start a fresh exploration without overwriting the current conversation. This is the fastest way to compare different creative or strategic directions side by side.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'product-chat-input',
        targetSelector: '[data-walkthrough="product-chat-input"]',
        title: 'Use Mentions And Context',
        summary: 'Mention assets so IMAI works from exact context.',
        description:
          'This composer understands the current product, version, and work surface. You can mention assets, groups, and other references directly in chat so IMAI works from precise context instead of text alone.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 1,
      },
      {
        id: 'product-versions',
        targetSelector: '[data-walkthrough="product-versions"]',
        title: 'Version History',
        summary: 'Snapshot the product to try directions safely.',
        description:
          'Each version captures a snapshot of your product. Create new versions to explore different directions without losing your previous work.',
        panelPosition: 'right',
        panelMode: 'floating',
        padding: 1,
      },
    ],
    mobileSteps: [
      {
        id: 'mobile-product-tabs',
        targetSelector: '[data-walkthrough="mobile-product-tabs"]',
        title: 'Choose Your Work Surface',
        summary: 'Each tab is a different work mode for this product.',
        description:
          'Swipe through these tabs to switch the product into different working modes. Media Studio is for visuals, Catalogue Data is for listings, Prints & Patterns is for repeatable design exploration, and tech-pack is for production workflows.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'mobile-product-chat',
        targetSelector: '[data-walkthrough="mobile-product-chat"]',
        title: 'Product Workspace Chat',
        summary: 'Open a product-aware chat for this surface.',
        description:
          'Tap to open a chat that stays tied to this product and the surface you are viewing. Ask for variations, style changes, or next steps, and IMAI responds with product-aware context.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
    ],
  },
  {
    key: 'product-media-studio',
    routePattern: '/products/[^/]+/media-studio$',
    readySelector: '[data-walkthrough="ms-rail-tabs"]',
    mobileReadySelector: '[data-walkthrough="ms-rail-tabs"]',
    steps: [
      {
        id: 'ms-section-switcher',
        targetSelector: '[data-walkthrough="ms-section-switcher"]',
        includeIfSelectorExists: '[data-walkthrough="ms-section-switcher"]',
        title: 'Switch Work Surfaces',
        summary:
          'Move between Media Studio, Catalogue, Prints, and Blueprints.',
        description:
          'A product has several work surfaces. Use this menu to move between Media Studio for visuals, Catalogue Data for commerce details, and the Prints & Blueprints surfaces — all for the same product and version.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 2,
        spotlightPadding: 2,
      },
      {
        id: 'ms-rail-tabs',
        targetSelector: '[data-walkthrough="ms-rail-tabs"]',
        includeIfSelectorExists: '[data-walkthrough="ms-rail-tabs"]',
        title: 'Create, Chats, and Properties',
        summary: 'Three modes for this surface, all in one rail.',
        description:
          'The rail switches between Create (generate visuals), Chats (product-aware conversations tied to this surface), and Properties (commerce-ready product details). Create is open by default.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 4,
      },
      {
        id: 'ms-brand-avatar',
        targetSelector: '[data-walkthrough="ms-brand-avatar"]',
        includeIfSelectorExists: '[data-walkthrough="ms-brand-avatar"]',
        title: 'Set Brand and Avatar',
        summary: 'Lock in a Brand and avatars before generating.',
        description:
          'Pick a Brand and the avatars you want featured before you generate. IMAI uses these so lifestyle and social shots stay on-brand and on-character across the whole batch.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 4,
        spotlightPadding: 4,
      },
      {
        id: 'ms-suggestions',
        targetSelector: '[data-walkthrough="ms-suggestions"]',
        includeIfSelectorExists: '[data-walkthrough="ms-suggestions"]',
        title: 'One-Click Generation',
        summary: 'Tailored suggestions — listings, lifestyle, ads, and more.',
        description:
          'IMAI suggests asset batches tailored to this product. Click any suggestion to open it, tweak the prompt, and generate — or run them all to fill out the gallery fast.',
        panelPosition: 'left',
        panelMode: 'floating',
        padding: 4,
      },
      {
        id: 'ms-gallery',
        targetSelector: '[data-walkthrough="ms-gallery"]',
        includeIfSelectorExists: '[data-walkthrough="ms-gallery"]',
        title: 'Your Asset Gallery',
        summary: 'Every generated image and video lands here.',
        description:
          'Generated images and videos appear here, grouped by type. Hover any asset to download, edit, upscale, or mention it in chat for another pass.',
        panelPosition: 'auto',
        panelMode: 'floating',
        padding: 0,
      },
    ],
  },
  {
    key: 'aesthetics',
    routePattern: '/aesthetics$',
    steps: [
      {
        id: 'aesthetics-create',
        targetSelector: '[data-walkthrough="aesthetics-create"]',
        title: 'Create an Aesthetic',
        summary: 'A style guide built from moodboard images.',
        description:
          'Aesthetics are style guides that shape how your product images look. Create one by uploading moodboard images that capture the visual direction you want.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'aesthetics-grid',
        targetSelector: '[data-walkthrough="aesthetics-grid"]',
        title: 'Your Style Library',
        summary: 'Browse styles; IMAI auto-picks the best fit.',
        description:
          "Browse existing aesthetics here. You don't need to pick one manually — when you generate product images, IMAI automatically selects the best aesthetic and builds the right world around your product.",
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
    ],
  },
  {
    key: 'branding',
    routePattern: '/branding$',
    steps: [
      {
        id: 'branding-create',
        targetSelector: '[data-walkthrough="branding-create"]',
        title: 'Add Your Brand',
        summary: 'Pull logos, colors, and fonts from a URL.',
        description:
          'Create a Brand entry by sharing your website URL or Pinterest board. AI extracts logos, colors, and typography automatically. You can also upload images directly.',
        panelPosition: 'bottom',
        panelMode: 'floating',
        padding: 0,
      },
      {
        id: 'branding-grid',
        targetSelector: '[data-walkthrough="branding-grid"]',
        title: 'Brand Library',
        summary: 'Your Brand identities for on-brand assets.',
        description:
          'Your analyzed Brands appear here. Each one captures your visual identity — logos, color palettes, typography, and social style. Use them to keep generated assets on-brand.',
        panelPosition: 'top',
        panelMode: 'floating',
        padding: 0,
      },
    ],
  },
];
